import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { setAuthToken } from '@/utils/auth';
import { exchangeSession } from '@/services/authService';
import { getVaultConfig, initializeVault } from '@/services/vaultService';
import { toast } from 'sonner';
import SplashLoader from '@/components/SplashLoader';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Completing authentication...');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        const hash = window.location.hash || location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('Authentication failed - no session ID');
          navigate('/login', { replace: true });
          return;
        }

        // Clear hash immediately to prevent re-processing on re-renders
        window.history.replaceState(null, '', window.location.pathname);

        setStatus('Verifying your identity...');
        const userData = await exchangeSession(sessionId);

        // Store the token for Bearer auth
        if (userData.token) setAuthToken(userData.token);

        setStatus('Setting up your session...');
        await new Promise((resolve) => {
          setUser(userData);
          setTimeout(resolve, 100);
        });

        // Initialize vault if user has one configured
        try {
          const vaultConfig = await getVaultConfig();
          if (vaultConfig.exists && vaultConfig.security_tier === 'vault') {
            // Vault users need to enter their password to unlock — mark tier so UI knows
            await initializeVault(null, null, 'vault', null);
          } else {
            await initializeVault(null, null, 'cloud', null);
          }
        } catch (e) {
          console.warn('Vault config check failed (non-blocking):', e);
        }

        toast.success(`Welcome, ${userData.name || userData.email}!`);

        // Check for pending invite from before login
        const pendingInvite = sessionStorage.getItem("pending_invite");
        if (pendingInvite) {
          sessionStorage.removeItem("pending_invite");
          navigate(`/invite/${pendingInvite}`, { replace: true });
          return;
        }

        // Check for pending invites (non-blocking)
        import('@/services/teamService').then(({ getMyInvites }) => {
          getMyInvites()
            .then(invites => {
              if (invites?.length > 0) {
                toast.info(`You have ${invites.length} pending project invitation(s)!`);
              }
            })
            .catch(() => {});
        });

        navigate('/', { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        console.error('Error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          config: error.config ? {
            url: error.config.url,
            method: error.config.method,
          } : null
        });
        
        const isNetworkError = !error.response;
        let msg;
        if (isNetworkError) {
          msg = 'Network error — please check your connection and try again.';
        } else {
          const detail = error.response?.data?.detail;
          const status = error.response?.status;
          msg = detail || `Server error (${status}): ${error.message}`;
        }
        toast.error(`Authentication failed: ${msg}`);
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <div className="text-center" data-testid="auth-callback-loading">
        <SplashLoader fullScreen={false} message={status} />
      </div>
    </div>
  );
}
